import { useCallback, useEffect, useRef } from "react";
import scripts from "../config/parcelTtsScripts.json";
import { assetUrl } from "../utils/assetUrl";
import type { SpeechPlaybackResult } from "./useSpeechSynthesis";

const assets = new Map(Object.values(scripts.assets).map(asset => [asset.spokenText, asset.audioSrc]));

/** Preload only the standalone demo; retain the shared player's stop/fallback behavior. */
export function useParcelSpeech(
  active: boolean,
  speak: (text: string) => SpeechPlaybackResult,
  speakAudio: (src: string, fallbackText: string) => SpeechPlaybackResult
) {
  const loaded = useRef(new Map<string, string>());

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    const urls = loaded.current;
    for (const [text, path] of assets) {
      void fetch(assetUrl(path), { signal: controller.signal, cache: "force-cache" })
        .then(response => {
          if (!response.ok || !response.headers.get("content-type")?.startsWith("audio/")) {
            throw new Error("Audio preload unavailable");
          }
          return response.blob();
        })
        .then(blob => {
          if (!controller.signal.aborted) urls.set(text, URL.createObjectURL(blob));
        })
        .catch(() => { /* Direct static playback and TTS remain available. */ });
    }
    return () => {
      controller.abort();
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, [active]);

  return useCallback((text: string) => {
    const content = text.trim();
    const path = active ? assets.get(content) : undefined;
    return path
      ? speakAudio(loaded.current.get(content) ?? assetUrl(path), content)
      : speak(text);
  }, [active, speak, speakAudio]);
}
